import type { CriticalHabitatSourceClosure, RefdataManifest } from '../../types/refdata';

export const USFWS_CH_SERVICE = 'https://services.arcgis.com/QVENGdaPbd4LUkLV/ArcGIS/rest/services/USFWS_Critical_Habitat/FeatureServer';
export const USFWS_CH_LAYERS = [
  { layerId: 0, designation: 'final', sourceUrl: `${USFWS_CH_SERVICE}/0` },
  { layerId: 2, designation: 'proposed', sourceUrl: `${USFWS_CH_SERVICE}/2` },
] as const;
export const USFWS_CH_FIELDS = ['OBJECTID', 'comname', 'sciname', 'listing_status', 'status', 'pubdate', 'effectdate', 'vacatedate', 'fedreg', 'unitname', 'subunitname'];

export function isCriticalHabitatSourceClosure(value: unknown, version: string): value is CriticalHabitatSourceClosure {
  if (!value || typeof value !== 'object') return false;
  const c = value as CriticalHabitatSourceClosure;
  return c.schemaVersion === 1 && c.version === version && /^[a-zA-Z0-9._-]+$/.test(version)
    && Array.isArray(c.cellIds) && c.cellIds.length > 0 && new Set(c.cellIds).size === c.cellIds.length
    && c.cellIds.every(id => typeof id === 'string' && /^[ns]\d{4}[ew]\d{5}_p50$/.test(id))
    && Array.isArray(c.layers) && c.layers.length === 2
    && USFWS_CH_LAYERS.every(expected => {
      const matches = c.layers.filter(layer => layer?.layerId === expected.layerId);
      if (matches.length !== 1) return false;
      const layer = matches[0]!;
      const raw = layer.raw;
      return layer.designation === expected.designation && layer.sourceUrl === expected.sourceUrl
        && Number.isSafeInteger(layer.sourceCount) && layer.sourceCount >= 0 && layer.featureCount === layer.sourceCount
        && typeof layer.vintageDate === 'string' && !!layer.vintageDate
        && !!raw && raw.path === `refdata/usfws-critical-habitat/${version}/raw/layer-${layer.layerId}.ndjson`
        && typeof raw.generation === 'string' && /^[1-9]\d*$/.test(raw.generation)
        && typeof raw.sha256 === 'string' && /^[a-f0-9]{64}$/.test(raw.sha256)
        && Number.isSafeInteger(raw.bytes) && raw.bytes > 0;
    });
}

/** Legacy proposed-only snapshots retain their positives, but cannot prove absence. */
export function validCriticalHabitatManifest(m: Partial<RefdataManifest>): boolean {
  if (m.criticalHabitatSources === undefined) return true;
  return m.datasetId === 'usfws-critical-habitat' && m.kind === 'vector'
    && m.cellSizeDeg === 0.5 && m.geometryStorage === 'cell-clipped'
    && typeof m.version === 'string' && isCriticalHabitatSourceClosure(m.criticalHabitatSources, m.version)
    && m.featureCount === m.criticalHabitatSources.layers.reduce((n, layer) => n + layer.featureCount, 0);
}
